import { Configuration } from './api/generated/apc/configuration';

export type AppModule = 'web' | 'mobile';

export interface AppWindow extends Window {
  ENV?: {
    EXPO_PUBLIC_BASE_URL: string;
    EXPO_PUBLIC_AMBIENT: string;
    EXPO_PUBLIC_BASE_URL_PORTALE_VERSION: string;
    EXPO_PUBLIC_MODULE: string;
    EXPO_PUBLIC_DOCUMENT_CONTENT_BASE_URL: string;
    EXPO_PUBLIC_ANAGRAFICA_BASE_URL: string;
    EXPO_PUBLIC_API_TIMEOUT: number;
    EXPO_PUBLIC_OLD_MYAZIMUT_URL?: string;
    EXPO_PUBLIC_FIREBASE_API_KEY?: string;
    EXPO_PUBLIC_FIREBASE_AUTH_DOMAIN?: string;
    EXPO_PUBLIC_FIREBASE_PROVIDER?: string;
    EXPO_PUBLIC_PREVINET_URL?: string;
    EXPO_PUBLIC_PREVINET_SUSTAINABLE_FUTURE_URL?: string;
    EXPO_PUBLIC_AZIMUT_LIFE_URL?: string;
    EXPO_PUBLIC_MORNINGSTAR_URL?: string;
    EXPO_PUBLIC_DOB_BASE_URL?: string;
    EXPO_PUBLIC_OW_BASE_URL?: string;
    EXPO_PUBLIC_PROMETEIA_BASE_URL?: string;
    EXPO_PUBLIC_PROMETEIA_TENANT_ID?: string;
    EXPO_PUBLIC_PROMETEIA_SOURCE_CODE?: string;
    EXPO_PUBLIC_PROMETEIA_LANGUAGE?: string;
    EXPO_PUBLIC_AZIMUT_INVYDIO_URL?: string;
    EXPO_PUBLIC_MARKETPLACE_URL?: string;
    BASE_DOMAIN?: string;
    EXPO_PUBLIC_BASE_URL_IDP_VERSION?: string;
    EXPO_PUBLIC_BASE_WEB_URL?: string;
    EXPO_PUBLIC_ICE_SERVERS?: string[];
    EXPO_PUBLIC_KID_BASE_URL?: string;
    EXPO_PUBLIC_ANDROID_PLAY_STORE_URL?: string;
    EXPO_PUBLIC_IOS_APP_STORE_URL?: string;
    EXPO_PUBLIC_AZIMUT_URL?: string;
    EXPO_PUBLIC_ADMINCONSOLE_MEMBEROFGROUP?: string;
    EXPO_PUBLIC_ADMINCONSOLE_FIDUCIARIE_MEMBEROFGROUP?: string;
    EXPO_PUBLIC_STORAGE_BASE_URL?: string;

    // Helm injected variables
    REACT_APP_BASE_URL?: string;
    REACT_APP_AMBIENT?: string;
    REACT_APP_BASE_URL_PORTALE_VERSION?: string;
    REACT_APP_MODULE?: string;
    REACT_APP_DOCUMENT_CONTENT_BASE_URL?: string;
    REACT_APP_ANAGRAFICA_BASE_URL?: string;
    REACT_APP_API_TIMEOUT?: number;
    REACT_APP_OLD_MYAZIMUT_URL?: string;
    REACT_APP_USE_MOCK?: boolean;
    REACT_APP_FIREBASE_API_KEY?: string;
    REACT_APP_FIREBASE_AUTH_DOMAIN?: string;
    REACT_APP_FIREBASE_PROVIDER?: string;
    REACT_APP_PREVINET_URL?: string;
    REACT_APP_PREVINET_SUSTAINABLE_FUTURE_URL?: string;
    REACT_APP_AZIMUT_LIFE_URL?: string;
    REACT_APP_MORNINGSTAR_URL?: string;
    REACT_APP_DOB_BASE_URL?: string;
    REACT_APP_OW_BASE_URL?: string;
    REACT_APP_PROMETEIA_BASE_URL?: string;
    REACT_APP_PROMETEIA_TENANT_ID?: string;
    REACT_APP_PROMETEIA_SOURCE_CODE?: string;
    REACT_APP_PROMETEIA_LANGUAGE?: string;
    REACT_APP_AZIMUT_INVYDIO_URL?: string;
    REACT_APP_MARKETPLACE_URL?: string;
    REACT_APP_BASE_DOMAIN?: string;
    REACT_APP_BASE_URL_IDP_VERSION?: string;
    REACT_APP_BASE_WEB_URL?: string;
    REACT_APP_ICE_SERVERS?: string[];
    REACT_APP_KID_BASE_URL?: string;
    REACT_APP_ANDROID_PLAY_STORE_URL?: string;
    REACT_APP_IOS_APP_STORE_URL?: string;
    REACT_APP_AZIMUT_URL?: string;
    REACT_APP_ADMINCONSOLE_MEMBEROFGROUP?: string;
    REACT_APP_ADMINCONSOLE_FIDUCIARIE_MEMBEROFGROUP?: string;
    REACT_APP_STORAGE_BASE_URL?: string;
  };
}

const appWindow: AppWindow = window;

class Env {
  constructor() {
    if (
      !appWindow.ENV?.REACT_APP_BASE_URL &&
      !process.env.EXPO_PUBLIC_BASE_URL
    ) {
      throw Error('Missing mandatory env variable: EXPO_PUBLIC_BASE_URL');
    }
    if (!appWindow.ENV?.REACT_APP_MODULE && !process.env.EXPO_PUBLIC_MODULE) {
      throw Error('Missing mandatory env variable: EXPO_PUBLIC_MODULE');
    }
    if (
      !appWindow.ENV?.REACT_APP_DOCUMENT_CONTENT_BASE_URL &&
      !process.env.EXPO_PUBLIC_DOCUMENT_CONTENT_BASE_URL
    ) {
      throw Error(
        'Missing mandatory env variable: EXPO_PUBLIC_DOCUMENT_CONTENT_BASE_URL'
      );
    }
    if (
      !appWindow.ENV?.REACT_APP_ANAGRAFICA_BASE_URL &&
      !process.env.EXPO_PUBLIC_ANAGRAFICA_BASE_URL
    ) {
      throw Error(
        'Missing mandatory env variable: EXPO_PUBLIC_ANAGRAFICA_BASE_URL'
      );
    }
  }

  get baseUrl(): string {
    return (
      appWindow.ENV?.REACT_APP_BASE_URL ||
      process.env.EXPO_PUBLIC_BASE_URL ||
      ''
    );
  }

  get ambient(): string {
    return (
      appWindow.ENV?.REACT_APP_AMBIENT || process.env.EXPO_PUBLIC_AMBIENT || ''
    );
  }

  get apiConfig(): Configuration {
    return {
      basePath: `${this.baseUrl}/${
        appWindow.ENV?.REACT_APP_BASE_URL_PORTALE_VERSION ||
        process.env.EXPO_PUBLIC_BASE_URL_PORTALE_VERSION
      }`,
      isJsonMime: () => true,
    };
  }

  get apiOpenIdUrl(): string {
    return `${this.baseUrl}/${
      appWindow.ENV?.REACT_APP_BASE_URL_IDP_VERSION ||
      process.env.EXPO_PUBLIC_BASE_URL_IDP_VERSION ||
      'idp/v1'
    }`;
  }

  get appModule(): AppModule {
    return appWindow.ENV?.REACT_APP_MODULE === 'mobile' ||
      process.env.EXPO_PUBLIC_MODULE === 'mobile'
      ? 'mobile'
      : 'web';
  }

  get firebaseApiKey(): string {
    return (
      appWindow.ENV?.REACT_APP_FIREBASE_API_KEY ||
      process.env.EXPO_PUBLIC_FIREBASE_API_KEY ||
      ''
    );
  }

  get firebaseAuthDomain(): string {
    return (
      appWindow.ENV?.REACT_APP_FIREBASE_AUTH_DOMAIN ||
      process.env.EXPO_PUBLIC_FIREBASE_AUTH_DOMAIN ||
      ''
    );
  }

  get firebaseProvider(): string {
    return (
      appWindow.ENV?.REACT_APP_FIREBASE_PROVIDER ||
      process.env.EXPO_PUBLIC_FIREBASE_PROVIDER ||
      ''
    );
  }

  get anagraficaBaseUrl(): string {
    return (
      appWindow.ENV?.REACT_APP_ANAGRAFICA_BASE_URL ||
      process.env.EXPO_PUBLIC_ANAGRAFICA_BASE_URL ||
      ''
    );
  }

  get documentContentBaseUrl(): string {
    return (
      appWindow.ENV?.REACT_APP_DOCUMENT_CONTENT_BASE_URL ||
      process.env.EXPO_PUBLIC_DOCUMENT_CONTENT_BASE_URL ||
      ''
    );
  }

  get previnetUrl(): string {
    return (
      appWindow.ENV?.REACT_APP_PREVINET_URL ||
      process.env.EXPO_PUBLIC_PREVINET_URL ||
      ''
    );
  }

  get previnetSustainableFutureUrl(): string {
    return (
      appWindow.ENV?.REACT_APP_PREVINET_SUSTAINABLE_FUTURE_URL ||
      process.env.EXPO_PUBLIC_PREVINET_SUSTAINABLE_FUTURE_URL ||
      ''
    );
  }

  get dobBaseUrl(): string {
    return (
      appWindow.ENV?.REACT_APP_DOB_BASE_URL ||
      process.env.EXPO_PUBLIC_DOB_BASE_URL
    );
  }

  get owBaseUrl(): string {
    return (
      appWindow.ENV?.REACT_APP_OW_BASE_URL ||
      process.env.EXPO_PUBLIC_OW_BASE_URL
    );
  }

  get prometeiaBaseUrl(): string {
    return (
      appWindow.ENV?.REACT_APP_PROMETEIA_BASE_URL ||
      process.env.EXPO_PUBLIC_PROMETEIA_BASE_URL
    );
  }

  get prometeiaTenantId(): string {
    return (
      appWindow.ENV?.REACT_APP_PROMETEIA_TENANT_ID ||
      process.env.EXPO_PUBLIC_PROMETEIA_TENANT_ID
    );
  }

  get prometeiaSourceCode(): string {
    return (
      appWindow.ENV?.REACT_APP_PROMETEIA_SOURCE_CODE ||
      process.env.EXPO_PUBLIC_PROMETEIA_SOURCE_CODE
    );
  }

  get prometeiaLanguageRequest(): string {
    return (
      appWindow.ENV?.REACT_APP_PROMETEIA_LANGUAGE ||
      process.env.EXPO_PUBLIC_PROMETEIA_LANGUAGE
    );
  }

  get invydioBaseUrl(): string {
    return (
      appWindow.ENV?.REACT_APP_AZIMUT_INVYDIO_URL ||
      process.env.EXPO_PUBLIC_AZIMUT_INVYDIO_URL
    );
  }

  get azimutLifeUrl(): string {
    return (
      appWindow.ENV?.REACT_APP_AZIMUT_LIFE_URL ||
      process.env.EXPO_PUBLIC_AZIMUT_LIFE_URL ||
      ''
    );
  }

  get morningstarUrl(): string {
    return (
      appWindow.ENV?.REACT_APP_MORNINGSTAR_URL ||
      process.env.EXPO_PUBLIC_MORNINGSTAR_URL ||
      ''
    );
  }
  get kidBaseUrl(): string {
    return (
      appWindow.ENV?.REACT_APP_KID_BASE_URL ||
      process.env.EXPO_PUBLIC_KID_BASE_URL ||
      ''
    );
  }

  get apiTimeout(): number {
    return (
      appWindow.ENV?.REACT_APP_API_TIMEOUT ||
      Number(process.env.EXPO_PUBLIC_API_TIMEOUT) ||
      10000
    );
  }
  get oldMyAzimutUrl(): string {
    return (
      appWindow.ENV?.REACT_APP_OLD_MYAZIMUT_URL ||
      process.env.EXPO_PUBLIC_OLD_MYAZIMUT_URL ||
      ''
    );
  }
  get marketplaceUrl(): string {
    return (
      appWindow.ENV?.REACT_APP_MARKETPLACE_URL ||
      process.env.EXPO_PUBLIC_MARKETPLACE_URL ||
      ''
    ).replace('setthenonce', new Date().getTime().toString());
  }
  get baseDomain(): string {
    return (
      appWindow.ENV?.REACT_APP_BASE_DOMAIN ||
      appWindow.ENV?.BASE_DOMAIN ||
      process.env.BASE_DOMAIN ||
      ''
    );
  }
  get baseWebUrl(): string {
    return (
      appWindow.ENV?.REACT_APP_BASE_WEB_URL ||
      process.env.EXPO_PUBLIC_BASE_WEB_URL ||
      ''
    );
  }
  get idpVersion(): string {
    return (
      appWindow.ENV?.REACT_APP_BASE_URL_IDP_VERSION ||
      process.env.EXPO_PUBLIC_BASE_URL_IDP_VERSION ||
      ''
    );
  }

  get iceServers(): string[] {
    return (
      appWindow.ENV?.REACT_APP_ICE_SERVERS ||
      process.env.EXPO_PUBLIC_ICE_SERVERS?.split(',') ||
      []
    );
  }

  get androidPlayStoreUrl(): string {
    return (
      appWindow.ENV?.REACT_APP_ANDROID_PLAY_STORE_URL ||
      process.env.EXPO_PUBLIC_ANDROID_PLAY_STORE_URL ||
      ''
    );
  }

  get iosAppStoreUrl(): string {
    return (
      appWindow.ENV?.REACT_APP_IOS_APP_STORE_URL ||
      process.env.EXPO_PUBLIC_IOS_APP_STORE_URL ||
      ''
    );
  }

  get azimutUrl(): string {
    return (
      appWindow.ENV?.REACT_APP_AZIMUT_URL ||
      process.env.EXPO_PUBLIC_AZIMUT_URL ||
      ''
    );
  }

  get adminConsoleMemberOfGroup(): string {
    return (
      appWindow.ENV?.REACT_APP_ADMINCONSOLE_MEMBEROFGROUP ||
      process.env.EXPO_PUBLIC_ADMINCONSOLE_MEMBEROFGROUP ||
      ''
    );
  }

  get adminConsoleFiduciarieMemberOfGroup(): string {
    return (
      appWindow.ENV?.REACT_APP_ADMINCONSOLE_FIDUCIARIE_MEMBEROFGROUP ||
      process.env.EXPO_PUBLIC_ADMINCONSOLE_FIDUCIARIE_MEMBEROFGROUP ||
      ''
    );
  }

  get storageBaseUrl(): string {
    return (
      appWindow.ENV?.REACT_APP_STORAGE_BASE_URL ||
      process.env.EXPO_PUBLIC_STORAGE_BASE_URL ||
      ''
    );
  }
}

export const env = new Env();
